import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Receipt, Archive, Wrench, Download, Trash2 } from "lucide-react";
import { api, errMsg, downloadBlob } from "../../lib/api";
import { Btn, Field, Input, Textarea, Section } from "../../components/kit";
import { PushSetup } from "../spec/Profile";
import ChangePassword from "../../components/ChangePassword";

function ServiceRow({ s }) {
  const [d, setD] = useState(s);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/services/${s.key}`, { ...d, default_price: Number(d.default_price), default_payout: Number(d.default_payout) });
      toast.success(`${s.name} saved`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="py-3 border-b border-[#1f1f1f] last:border-0 space-y-2" data-testid={`service-row-${s.key}`}>
      <div className="flex justify-between text-sm"><span>{s.name}</span><span className="text-xs text-neutral-500">{s.category}</span></div>
      <div className="grid grid-cols-3 gap-2">
        <Input type="number" step="0.01" value={d.default_price} onChange={(e) => setD({ ...d, default_price: e.target.value })} placeholder="Start price" data-testid={`service-price-${s.key}`} />
        <Input type="number" step="0.01" value={d.default_payout} onChange={(e) => setD({ ...d, default_payout: e.target.value })} placeholder="Payout" data-testid={`service-payout-${s.key}`} />
        <Btn variant="outline" onClick={save} loading={busy} data-testid={`service-save-${s.key}`}>Save</Btn>
      </div>
      <div className="flex gap-3 text-xs text-neutral-400 items-center">
        <label className="flex gap-1.5 items-center"><input type="checkbox" checked={d.requires_equipment} onChange={(e) => setD({ ...d, requires_equipment: e.target.checked })} className="accent-[#FF6B00]" />Equipment-dependent</label>
        <label className="flex gap-1.5 items-center"><input type="checkbox" checked={d.active} onChange={(e) => setD({ ...d, active: e.target.checked })} className="accent-[#FF6B00]" />Active</label>
      </div>
      {d.requires_equipment && <Input className="h-9 text-sm" value={d.equipment_note} onChange={(e) => setD({ ...d, equipment_note: e.target.value })} placeholder="Required equipment (auto-fills job)" />}
    </div>
  );
}

export default function SettingsPage() {
  const [s, setS] = useState(null);
  const [services, setServices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);
  if (!s) return <div className="h-40 animate-pulse bg-[#141414] rounded-2xl" />;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", {
        tax_rate: Number(s.tax_rate), group_size: Number(s.group_size), offer_timeout_sec: Number(s.offer_timeout_sec),
        retention_days: s.retention_days ? Number(s.retention_days) : null, ack_text: s.ack_text,
      });
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const removeDemo = async () => {
    if (!window.confirm("Permanently remove all DEMO specialists and DEMO jobs?")) return;
    try {
      const { data } = await api.delete("/admin/demo");
      toast.success(`Removed ${data.jobs_removed} demo jobs, ${data.specialists_removed} demo specialists`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  return (
    <div className="space-y-4 fade-in">
      <h1 className="font-head text-3xl uppercase font-bold">Settings</h1>
      <Section title="Job offers" icon={Send}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nearest group size"><Input type="number" min="1" value={s.group_size} onChange={set("group_size")} data-testid="settings-group-size" /></Field>
          <Field label="Expand after (seconds)"><Input type="number" min="30" value={s.offer_timeout_sec} onChange={set("offer_timeout_sec")} data-testid="settings-timeout" /></Field>
        </div>
      </Section>
      <Section title="Tax & acknowledgment" icon={Receipt}>
        <Field label="Default sales tax rate %" hint="Confirm per job for the service address. Tax is kept separate from Driven earnings."><Input type="number" step="0.01" value={s.tax_rate} onChange={set("tax_rate")} data-testid="settings-tax-rate" /></Field>
        <Field label="Completion acknowledgment text"><Textarea value={s.ack_text} onChange={set("ack_text")} data-testid="settings-ack-text" /></Field>
      </Section>
      <Section title="Records retention" icon={Archive}>
        <Field label="Retention period (days)" hint="Blank = keep everything. Nothing is deleted automatically; dispute-hold jobs are always preserved."><Input type="number" value={s.retention_days || ""} onChange={set("retention_days")} data-testid="settings-retention" /></Field>
      </Section>
      <Btn size="lg" className="w-full" onClick={save} loading={busy} data-testid="settings-save-btn">Save settings</Btn>
      <Section title="Starting prices" icon={Wrench} testid="services-catalog">
        <div className="text-xs text-neutral-500">Start price · Specialist payout. Editable per job. No automatic market pricing.</div>
        {services.map((x) => <ServiceRow key={x.key} s={x} />)}
      </Section>
      <Section title="Export & data" icon={Download}>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)} className="accent-[#FF6B00]" data-testid="export-include-demo" />Include demo data</label>
        <Btn variant="outline" className="w-full" onClick={() => downloadBlob(`/admin/export/jobs.csv?include_demo=${demo}`, "driven-jobs.csv").catch((e) => toast.error(errMsg(e)))} data-testid="export-csv-btn"><Download className="w-4 h-4" />Download jobs CSV</Btn>
        <Btn variant="danger" className="w-full" onClick={removeDemo} data-testid="remove-demo-btn"><Trash2 className="w-4 h-4" />Remove demo data</Btn>
      </Section>
      <PushSetup />
      <ChangePassword />
    </div>
  );
}
